/** UXP-00018 — Customer Success Platform framework (mirrors pkg/customersuccess) */

export const UXP_CS_ID = 'UXP-00018';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface OnboardingStepRow {
  step: string;
  title: string;
  status: string;
  progress: number;
}

export interface AdoptionMetricRow {
  metric: string;
  value: string;
  change: string;
  target: string;
}

export interface GuidanceRow {
  feature: string;
  title: string;
  users: number;
  complete: number;
}

export interface CoachSessionRow {
  capability: string;
  title: string;
  detail: string;
}

export interface MilestoneRow {
  milestone: string;
  title: string;
  status: string;
  date: string;
}

export interface DemoEnvRow {
  feature: string;
  name: string;
  status: string;
  users: number;
}

export interface ReadinessCheckRow {
  check: string;
  status: string;
  score: number;
}

export const COMMAND_CENTER_METRICS = [
  'Customer Health', 'Activation', 'Adoption', 'Business Value', 'ROI',
  'Renewal Risk', 'Expansion Opportunity', 'AI Usage', 'Marketplace Status',
];

export const ONBOARDING_STEPS = [
  'Organization Setup', 'Marketplace Connection', 'Brand Discovery', 'Product Synchronization',
  'DNA Generation', 'Knowledge Initialization', 'AI Validation',
];

export const ADOPTION_METRICS = [
  'Daily Active Users', 'Weekly Active Users', 'Monthly Active Users', 'Workspace Adoption',
  'Feature Adoption', 'Automation Adoption', 'Knowledge Usage',
];

export const GUIDANCE_FEATURES = [
  'Role-based Tours', 'Walkthroughs', 'Tooltips', 'Contextual Help', 'Best Practices', 'In-app Coaching',
];

export const COACH_CAPABILITIES = [
  'Business Coaching', 'Implementation Guidance', 'Marketplace Advice',
  'Training', 'Optimization Suggestions', 'Success Planning',
];

export const SUCCESS_MILESTONES = [
  'Marketplace Connected', 'Products Imported', 'First AI Listing', 'First AI Creative',
  'First Publish', 'First Campaign', 'First Automation', 'Business Wins',
];

export const DEMO_FEATURES = [
  'Demo Organizations', 'Demo Data', 'Sandbox Workspace', 'Scenario Library',
  'Environment Reset', 'Safe Experimentation',
];

export const HEALTH_FACTORS = [
  'Usage', 'Business Outcomes', 'Support Load', 'Marketplace Health',
  'Growth', 'Renewal Risk', 'Expansion Potential',
];

export const SUCCESS_PLAYBOOKS = [
  'New Seller', 'Growing Seller', 'Enterprise Seller', 'Prime Day',
  'Seasonal Campaigns', 'International Expansion', 'Recovery Plans',
];

export const PRODUCTION_READINESS_CHECKS = [
  'Regression', 'Performance', 'Security', 'Accessibility', 'Marketplace APIs',
  'AI Services', 'Infrastructure', 'Backup', 'Disaster Recovery',
];

export const FEEDBACK_TYPES = [
  'NPS', 'CSAT', 'CES', 'Feature Requests', 'Bug Reports', 'AI Feedback', 'Marketplace Feedback',
];

export const SUCCESS_ANALYTICS = [
  'Activation', 'Adoption', 'Retention', 'Expansion', 'Business Outcomes', 'Feature Usage',
];

export const COLLABORATION_FEATURES = [
  'Ideas', 'Voting', 'Templates', 'Knowledge Sharing', 'Prompt Sharing', 'Events',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Customer Success Command Center', 'Business Onboarding', 'Product Adoption', 'Interactive Guidance',
  'AI Success Coach', 'Success Milestones', 'Demo & Sandbox', 'Production Readiness',
];

export const LIVE_ROUTES: Record<string, string> = {
  alpha: '/alpha',
  onboarding: '/onboarding',
  marketplaceConnection: '/platform/marketplace-connection',
  executiveHome: '/platform/executive-home',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Customer Health', value: '86/100', change: '+5', positive: true },
  { label: 'Activation', value: '92%', change: '+8%', positive: true },
  { label: 'Adoption', value: '78%', change: '+12%', positive: true },
  { label: 'Business Value', value: '₹14.6L/mo', change: '+18%', positive: true },
  { label: 'ROI', value: '4.2x', change: '+0.6x', positive: true },
  { label: 'Renewal Risk', value: 'Low', change: '2 at-risk', positive: true },
  { label: 'Expansion Opportunity', value: '3 accounts', change: '₹8.2L ARR', positive: true },
  { label: 'AI Usage', value: '84%', change: '+15%', positive: true },
  { label: 'Marketplace Status', value: 'Connected', change: 'Amazon.in active', positive: true },
];

export const DEMO_ONBOARDING: OnboardingStepRow[] = [
  { step: 'Organization Setup', title: 'Organization Setup', status: 'completed', progress: 100 },
  { step: 'Marketplace Connection', title: 'Marketplace Connection', status: 'completed', progress: 100 },
  { step: 'Brand Discovery', title: 'Brand Discovery', status: 'completed', progress: 100 },
  { step: 'Product Synchronization', title: 'Product Synchronization', status: 'in_progress', progress: 72 },
  { step: 'DNA Generation', title: 'DNA Generation', status: 'pending', progress: 0 },
  { step: 'Knowledge Initialization', title: 'Knowledge Initialization', status: 'pending', progress: 0 },
  { step: 'AI Validation', title: 'AI Validation', status: 'pending', progress: 0 },
];

export const DEMO_ADOPTION: AdoptionMetricRow[] = [
  { metric: 'Daily Active Users', value: '24 users', change: '+18%', target: '20' },
  { metric: 'Weekly Active Users', value: '42 users', change: '+12%', target: '35' },
  { metric: 'Monthly Active Users', value: '48 users', change: '+8%', target: '45' },
  { metric: 'Feature Adoption', value: '68%', change: '+15%', target: '70%' },
  { metric: 'Automation Adoption', value: '42%', change: '+22%', target: '50%' },
];

export const DEMO_GUIDANCE: GuidanceRow[] = [
  { feature: 'Role-based Tours', title: 'Brand Owner Tour', users: 18, complete: 14 },
  { feature: 'Walkthroughs', title: 'First Listing Walkthrough', users: 24, complete: 20 },
  { feature: 'In-app Coaching', title: 'Prime Day Prep Coach', users: 12, complete: 8 },
];

export const DEMO_COACH: CoachSessionRow[] = [
  { capability: 'Implementation Guidance', title: 'Complete product sync before Prime Day', detail: '842 products pending sync — estimated 2h · unlocks listing optimizer' },
  { capability: 'Marketplace Advice', title: 'Enable FBA for top 20 SKUs', detail: 'Predicted +18% conversion · reduces delivery time to 2 days' },
  { capability: 'Success Planning', title: 'Q3 success plan — reach ₹60L/mo revenue', detail: '3 milestones · 6-week timeline · 82% confidence' },
];

export const DEMO_MILESTONES: MilestoneRow[] = [
  { milestone: 'Marketplace Connected', title: 'Marketplace Connected', status: 'achieved', date: 'Jul 15' },
  { milestone: 'Products Imported', title: 'Products Imported (8,420)', status: 'achieved', date: 'Jul 18' },
  { milestone: 'First AI Listing', title: 'First AI Listing', status: 'achieved', date: 'Jul 22' },
  { milestone: 'First AI Creative', title: 'First AI Creative', status: 'achieved', date: 'Jul 25' },
  { milestone: 'First Publish', title: 'First Publish', status: 'in_progress', date: '—' },
  { milestone: 'First Campaign', title: 'First Campaign', status: 'pending', date: '—' },
];

export const DEMO_SANDBOX: DemoEnvRow[] = [
  { feature: 'Demo Organizations', name: 'TechHub Demo Org', status: 'active', users: 8 },
  { feature: 'Sandbox Workspace', name: 'Prime Day Sandbox', status: 'active', users: 4 },
  { feature: 'Scenario Library', name: 'New Seller Scenario', status: 'available', users: 0 },
];

export const DEMO_READINESS: ReadinessCheckRow[] = [
  { check: 'Regression', status: 'passed', score: 100 },
  { check: 'Performance', status: 'passed', score: 96 },
  { check: 'Security', status: 'passed', score: 98 },
  { check: 'Accessibility', status: 'passed', score: 94 },
  { check: 'Marketplace APIs', status: 'passed', score: 99 },
  { check: 'AI Services', status: 'passed', score: 97 },
  { check: 'Infrastructure', status: 'passed', score: 100 },
  { check: 'Backup', status: 'passed', score: 100 },
  { check: 'Disaster Recovery', status: 'passed', score: 95 },
];

export function buildCustomerSuccessDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    onboarding_steps: ONBOARDING_STEPS.length,
    adoption_metrics: ADOPTION_METRICS.length,
    success_milestones: SUCCESS_MILESTONES.length,
    readiness_checks: PRODUCTION_READINESS_CHECKS.length,
  };
}
